import { Injectable } from '@angular/core';
import { ApolloService } from '../apollo.service';
import * as fromGraphQl from './graphql';
import { Observable, of, switchMap, take } from 'rxjs';
import { CustomAttributeDefinitionInput, CustomAttributeSection, CustomAttributeSectionInput } from '../../entities/customAttributes/customAttributes';
/**
 * The users service.
 */
@Injectable()
export class CustomAttributesService {

  constructor(
    private apolloService: ApolloService
  ) {
  }

  public getNextCustomAttributeSectionIndex(customAttributeSections: CustomAttributeSection[]): number {
    if (!customAttributeSections || customAttributeSections.length === 0) {
      return 0;
    }
    // Find the maximum index in the existing sections
    const maxIndex = customAttributeSections.reduce((max, section) => {
      return section.index > max ? section.index : max;
    }, -1);
    // Return the next index
    return maxIndex + 1;
  }
  
  /**
   * Updates a custom attribute section
   * @param customAttributeSectionInput The input object for the custom attribute section
   * @param callback Callback function to handle the response
   */
  public update(customAttributeSectionInput: CustomAttributeSectionInput, callback: (arg: any) => void): void {
    this.apolloService.mutateAndExecute(
      fromGraphQl.UPDATE_CUSTOM_ATTRIBUTE_SECTION_MUTATION,
      {
        input: customAttributeSectionInput,
      },
      callback
    );
  }

  /**
   * Creates a new custom attribute section
   * @param customAttributeSectionInput The input object for the custom attribute section
   * @param callback Callback function to handle the response
   */
  public create(customAttributeSectionInput: CustomAttributeSectionInput, callback: any): void {
    this.apolloService.mutateAndExecute(
      fromGraphQl.CREATE_CUSTOM_ATTRIBUTE_SECTION_MUTATION,
      {
        input: customAttributeSectionInput,
      },
      callback
    );
  }

  public delete(customAttributeSectionId: string, callback: any): void {
    this.apolloService.mutateAndExecute(
      fromGraphQl.DELETE_CUSTOM_ATTRIBUTE_SECTION_MUTATION,
      {
        id: customAttributeSectionId,
      },
      callback
    );
  }

  public updateDefinition(customAttributeDefinitionInput: CustomAttributeDefinitionInput, callback: (arg: any) => void): void {
    this.apolloService.mutateAndExecute(
      fromGraphQl.UPDATE_CUSTOM_ATTRIBUTE_DEFINITION_MUTATION,
      {
        input: customAttributeDefinitionInput,
      },
      callback
    );
  }

  public createDefinition(customAttributeDefinitionInput: CustomAttributeDefinitionInput, callback: any): void {
    this.apolloService.mutateAndExecute(
      fromGraphQl.CREATE_CUSTOM_ATTRIBUTE_DEFINITION_MUTATION,
      {
        input: customAttributeDefinitionInput,
      },
      callback
    );
  }

  public deleteDefinition(customAttributeDefinitionId: string, callback: any): void {
    this.apolloService.mutateAndExecute(
      fromGraphQl.DELETE_CUSTOM_ATTRIBUTE_DEFINITION_MUTATION,
      {
        id: customAttributeDefinitionId,
      },
      callback
    );
  }
  
  /**
   * fetches a single event subscription by id
   * @param id event subscription id
   */
  public getCustomAttributeSections(entity: string): Observable<CustomAttributeSection[]> {
    return this.apolloService
      .query<fromGraphQl.CustomAttributeSectionsByEntityResponse>(
        fromGraphQl.CUSTOM_ATTRIBUTE_SECTIONS_BY_ENTITY_QUERY, 
        { 
          entity: entity,
          sortProperty: 'index',
          sortDirection: 'asc'
        })
      .pipe(
        take(1),
        switchMap((result) => {
          return of(CustomAttributeSection.createCustomAttributeSections(result?.data?.customAttributeSections));
        })
      );
  }

}

